import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { Field, SuccessPanel } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero overline="Contact" title="CONTACT FRESH START" italic="Furnishings." testid="contact-headline">
        Questions about donating, shopping, or partnering? We'd love to hear from you.
      </PageHero>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        {done ? (
          <SuccessPanel
            testid="contact-success-message"
            title="MESSAGE SENT — THANK YOU!"
            message="We've received your message and will get back to you as soon as we can."
          />
        ) : (
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <FadeUp>
              <div className="space-y-5">
                <a
                  data-testid="contact-email-link"
                  href="mailto:freshstartfurnishings@yahoo.com"
                  className="flex items-center gap-4 rounded-2xl border border-linen bg-white/60 p-6 transition-colors hover:border-burgundy/40"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-olive">
                    <Mail className="h-5 w-5 text-cream" />
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-burgundy">Email Us</p>
                    <p className="mt-1 break-all text-sm font-semibold text-olive">freshstartfurnishings@yahoo.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-2xl border border-linen bg-white/60 p-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-burgundy">
                    <MapPin className="h-5 w-5 text-cream" />
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-burgundy">Service Area</p>
                    <p className="mt-1 text-sm font-semibold text-olive">Glendale, the West Valley &amp; Surrounding Areas</p>
                  </div>
                </div>
                <p className="rounded-2xl bg-beige p-6 font-serif text-lg italic leading-relaxed text-olive">
                  "Every message — a couch offered, a question asked, a hand raised to help — moves a family closer to home."
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.15}>
              <form onSubmit={submit} className="space-y-5 rounded-2xl border border-linen bg-white/60 p-8" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name" required testid="contact-name-field">
                    <input data-testid="contact-name-input" required className="input-field" value={form.name} onChange={set("name")} />
                  </Field>
                  <Field label="Email" required testid="contact-email-field">
                    <input data-testid="contact-email-input" required type="email" className="input-field" value={form.email} onChange={set("email")} />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Phone" testid="contact-phone-field">
                    <input data-testid="contact-phone-input" type="tel" className="input-field" value={form.phone} onChange={set("phone")} />
                  </Field>
                  <Field label="Subject" testid="contact-subject-field">
                    <input data-testid="contact-subject-input" className="input-field" value={form.subject} onChange={set("subject")} />
                  </Field>
                </div>
                <Field label="Message" required testid="contact-message-field">
                  <textarea data-testid="contact-message-input" required rows={5} className="input-field" value={form.message} onChange={set("message")} />
                </Field>
                <button type="submit" data-testid="contact-form-submit-button" disabled={submitting} className="btn-primary w-full !py-4 disabled:opacity-60">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            </FadeUp>
          </div>
        )}
      </section>
    </div>
  );
}
